import { conjugateVerb, TE_FORM_LABEL } from "./verbConjugation";

// The ている ("be doing / be in a state") forms of a verb: its te-form plus いる,
// with いる conjugated as the ichidan verb it is.
//
// There is deliberately no te-form logic in this file. conjugateVerb already
// derives the te-form for every conjugation class (godan by final kana and the
// 行く／問う／ずる exceptions, ichidan, する, 来る), and each ている form is that
// exact form with the いる ending appended - so the two can never disagree.
// conjugateVerb itself is left untouched (the worksheet generator and the
// vocabulary page build their form lists from it), which is why these eight
// forms are a separate list rather than extra rows in its output.

export interface VerbForm {
    label: string;
    kanji: string;
    reading: string;
}

export type TeIruFormKey =
    | "casual"
    | "casualNegative"
    | "casualPast"
    | "casualNegativePast"
    | "polite"
    | "politeNegative"
    | "politePast"
    | "politeNegativePast";

export interface TeIruFormSpec {
    key: TeIruFormKey;
    // Stable English label. It keys the per-form listen counts and the romaji
    // reveal state, so it must stay unique among every form label of a verb.
    label: string;
    // The ている ending as written after the te-form's て／で (shown on its own in
    // the row heading); the form is te-form + auxiliary, e.g. 食べて + いない.
    pattern: string;
    // いる conjugated for this form, appended to the te-form.
    auxiliary: string;
}

export interface TeIruForm extends VerbForm {
    key: TeIruFormKey;
    pattern: string;
}

export const TE_IRU_FORM_SPECS: readonly TeIruFormSpec[] = [
    { key: "casual", label: "ている — Casual", pattern: "ている", auxiliary: "いる" },
    { key: "casualNegative", label: "ていない — Casual negative", pattern: "ていない", auxiliary: "いない" },
    { key: "casualPast", label: "ていた — Casual past", pattern: "ていた", auxiliary: "いた" },
    {
        key: "casualNegativePast",
        label: "ていなかった — Casual negative past",
        pattern: "ていなかった",
        auxiliary: "いなかった",
    },
    { key: "polite", label: "ています — Polite", pattern: "ています", auxiliary: "います" },
    { key: "politeNegative", label: "ていません — Polite negative", pattern: "ていません", auxiliary: "いません" },
    { key: "politePast", label: "ていました — Polite past", pattern: "ていました", auxiliary: "いました" },
    {
        key: "politeNegativePast",
        label: "ていませんでした — Polite negative past",
        pattern: "ていませんでした",
        auxiliary: "いませんでした",
    },
];

// The ている forms of a verb, built from the forms conjugateVerb returned for
// it. null when there is no te-form to build on (a class the engine doesn't
// cover), so a verb with no conjugation table gets no ている group either.
export function teIruFormsFrom(forms: readonly VerbForm[] | null | undefined): TeIruForm[] | null {
    const teForm = forms?.find((form) => form.label === TE_FORM_LABEL);
    if (!teForm) return null;
    return TE_IRU_FORM_SPECS.map((spec) => ({
        key: spec.key,
        label: spec.label,
        pattern: spec.pattern,
        kanji: teForm.kanji + spec.auxiliary,
        reading: teForm.reading + spec.auxiliary,
    }));
}

export function conjugateTeIru(word: string, reading: string, verbType: string): TeIruForm[] | null {
    return teIruFormsFrom(conjugateVerb(word, reading, verbType) as VerbForm[] | null);
}

// Splits a ている form's reading into the te-form and the いる ending, for the
// romaji (which writes いる as its own word: "tabete iru"). null for any other
// form, or a reading that doesn't end the way the form should.
export function splitTeIruReading(label: string, reading: string): { teForm: string; auxiliary: string } | null {
    const spec = TE_IRU_FORM_SPECS.find((candidate) => candidate.label === label);
    if (!spec || !reading.endsWith(spec.auxiliary)) return null;
    return { teForm: reading.slice(0, -spec.auxiliary.length), auxiliary: spec.auxiliary };
}

// Verbs whose ている forms are mechanically correct but rarely or never used in
// their main sense, so their card carries a short note instead of hiding or
// altering the forms.
//  - stative: the verb already describes a state (existing, being, needing).
//  - dekiru:  できる meaning "can" does not take ている, but できる meaning "to be
//             made / finished / ready" does, very commonly: 準備ができている
//             ("is ready"), 木でできている ("is made of wood").
//  - shiru:   知っている is common; its negative is 知らない, never 知っていない.
export type TeIruNote = "stative" | "dekiru" | "shiru";

// "headword|reading" keys, matching how the vocabulary data spells each entry.
const TE_IRU_NOTES: Readonly<Record<string, TeIruNote>> = {
    "有る|ある": "stative",
    "ある|ある": "stative",
    "居る|いる": "stative",
    "いる|いる": "stative", // the rebuilt data (NIH-281) shows 居る (to be) in kana
    "居る|おる": "stative",
    "要る|いる": "stative",
    "出来る|できる": "dekiru",
    "できる|できる": "dekiru",
    "知る|しる": "shiru",
};

export function teIruNoteFor(word: string, reading: string): TeIruNote | null {
    return TE_IRU_NOTES[`${word}|${reading}`] ?? null;
}
